/**
 * The little strings a player's screen sends so others can see what they are doing: a guest
 * watching from their own phone, and the TV showing the table a couple of players at a time.
 *
 * These ride on the 10 Hz state patch, so they have to stay tiny. The encoder and the decoders
 * live together, and in show-core, because the game writes the frame and two different apps read
 * it: when the rhythm game grew from one coil to three the two sides drifted apart and every
 * watcher quietly saw an empty coil bank. Keeping the pair together, and tested, stops that.
 *
 * A frame only ever describes the board: never a role, a vote or a sabotage.
 */

export interface RhythmPulseFrame {
  /** Which coil it is falling down. */
  lane: number;
  /** How far it has travelled, 0 at the top and 1 at the line. */
  t: number;
}

/** `p0:2@0.45|p1:0@0.10` — at most three pulses, nearest first. */
export function encodeRhythmFrame(pulses: readonly RhythmPulseFrame[]): string {
  return pulses
    .slice(0, 3)
    .map((p, i) => `p${i}:${p.lane}@${p.t.toFixed(2)}`)
    .join('|');
}

export function decodeRhythmPulse(frame: string, index: number): RhythmPulseFrame | null {
  const m = new RegExp(`p${index}:(\\d)@(-?[\\d.]+)`).exec(frame);
  if (!m) return null;
  const lane = Number(m[1]);
  const t = Number(m[2]);
  if (!Number.isFinite(lane) || !Number.isFinite(t)) return null;
  return { lane, t };
}

/** A number carried under `key` (`y:42`), or the fallback when the frame has none. */
export function frameNumber(frame: string, key: string, fallback = 0): number {
  // A key starts the frame or follows a `|` or a `,` (a thread gate is `g0x:80,g0c:35`).
  const m = new RegExp(`(?:^|[|,])${key}:(-?[\\d.]+)`).exec(frame);
  return m ? Number(m[1]) : fallback;
}

/** THREAD: the ship's height and up to three gates, all in percent of the field. */
export function decodeThreadFrame(frame: string): { y: number; gates: { x: number; centre: number }[] } {
  const gates: { x: number; centre: number }[] = [];
  for (let i = 0; i < 3; i++) {
    const x = frameNumber(frame, `g${i}x`, -20);
    if (x < -10) continue;
    gates.push({ x, centre: frameNumber(frame, `g${i}c`, 50) });
  }
  return { y: frameNumber(frame, 'y', 50), gates };
}

/** VINE: whether cell `i` of the 11×9 bed is grown. The frame is `12,13,14|s:40`: the vine, then its seed. */
export function vineCellOn(frame: string, i: number): boolean {
  return (frame.split('|')[0] ?? '').split(',').includes(String(i));
}

/** RECALL: whether card `i` is face up. */
export const recallCardUp = (frame: string, i: number): boolean => frame.includes(`u${i}`);

/** DIVIDE: whether portion `i` has been cut. */
export const divideCut = (frame: string, i: number): boolean => frame.includes(`d${i}`);

/** FORAGE: where the candle is, in percent of the board, and how many are picked — `x:40|y:62|f:2`. */
export function encodeForageFrame(x: number, y: number, found: number): string {
  return `x:${Math.round(x * 100)}|y:${Math.round(y * 100)}|f:${found}`;
}

/** FORAGE: the candle's place in percent, or null before the first touch. */
export function decodeForageFrame(frame: string): { x: number; y: number; found: number } | null {
  if (!frame.includes('x:')) return null;
  return { x: frameNumber(frame, 'x', 50), y: frameNumber(frame, 'y', 50), found: frameNumber(frame, 'f', 0) };
}
